import { create } from 'zustand';
import { persist, createJSONStorage, StateStorage } from 'zustand/middleware';
import LocalDateTime from '~/lib/localdatetime';

// Custom JSON serialization to handle LocalDateTime and Date objects
const customReplacer = (_key: string, value: unknown): unknown => {
    if (value instanceof LocalDateTime) {
        return { __type: 'LocalDateTime', data: value.toJson() };
    }
    if (value instanceof Date) {
        return { __type: 'Date', data: value.toISOString() };
    }
    return value;
};

const customReviver = (_key: string, value: unknown): unknown => {
    if (value && typeof value === 'object' && value !== null) {
        const obj = value as { __type?: string; data?: unknown };
        if (obj.__type === 'LocalDateTime' && obj.data) {
            return LocalDateTime.deserialize(obj.data as { localDatetime: string; zone: string });
        }
        if (obj.__type === 'Date' && typeof obj.data === 'string') {
            return new Date(obj.data);
        }
    }
    return value;
};

// Custom storage with serialization for special types
const customStorage: StateStorage = {
    getItem: (name: string): string | null => {
        const str = localStorage.getItem(name);
        if (!str) {
            return null;
        }
        return str;
    },
    setItem: (name: string, value: string): void => {
        // Parse, transform with replacer, then stringify again
        const parsed = JSON.parse(value);
        const transformed = JSON.parse(JSON.stringify(parsed, customReplacer));
        localStorage.setItem(name, JSON.stringify(transformed));
    },
    removeItem: (name: string): void => {
        localStorage.removeItem(name);
    },
};

export const digitalStore = {
    id: -1,
    name: "Kalia Lab Digital",
    slug: 'digital',
    contactEmail: "info@kontakt.kalialab.de",
    contactPhone: "+49 30 255 596 60",
};

export type DayOfWeek = 'monday' | 'tuesday' | 'wednesday' | 'thursday' | 'friday' | 'saturday' | 'sunday';

// Add-on selected by customer
export type SelectedAddOn = {
    addOnId: string;
    addOnNameDe: string;
    addOnNameEn: string;
    variantId: string;
    variantName: string;
    price: number;
    durationMinutes: number;
}

// Cart item type using BookableItem abstraction
export type CartItem = {
    // BookableItem info
    bookableItemId: string;
    bookableItemNameDe: string;
    bookableItemNameEn: string;
    bookableItemDescriptionDe: string;
    bookableItemDescriptionEn: string;
    bookableItemImageUrl?: string;
    bookableItemType: 'SERVICE' | 'BUNDLE';

    // Selected variant (from variant options)
    selectedVariantId: string;
    selectedVariantName: string;
    selectedVariantOptionLabelDe: string;
    selectedVariantOptionLabelEn: string;
    price: number;
    durationMinutes: number;

    // Selected add-ons (only for SERVICE type)
    addOns: SelectedAddOn[];

    // Bundle info (only for BUNDLE type)
    bundleId?: string;
    bundleName?: string;
    bundleUnitPriceGross?: number;
    bundleUnitQuantity?: number;

    // When true, this item redeems an existing bundle voucher instead of purchasing the bundle.
    isBundleRedemption?: boolean;

    // CMS category name (from BookingFunnelCategory), used for GTM tracking
    categoryNameDe?: string;
}

// Helper to calculate total price of a cart item including add-ons
export const getCartItemTotalPrice = (item: CartItem): number => {
    const addOnsTotal = item.addOns.reduce((sum, addon) => sum + addon.price, 0);
    return item.price + addOnsTotal;
};

// GTM price for a cart item's main service (zeroes out bundle redemptions).
export const getCartItemGtmPrice = (item: CartItem): number => {
    if (item.isBundleRedemption) return 0;
    return item.price;
};

// GTM total for a cart item: main service GTM price + add-ons (add-ons always at full price).
export const getCartItemGtmTotalPrice = (item: CartItem): number => {
    const addOnsTotal = item.addOns.reduce((sum, addon) => sum + addon.price, 0);
    return getCartItemGtmPrice(item) + addOnsTotal;
};

const getCartItemGtmCategory = (item: CartItem): string => {
    return item.categoryNameDe ?? '';
};

// Flatten cart items into GTM-ready line items, including add-ons as separate entries.
export const toGtmCartItems = (cartItems: CartItem[]) =>
    cartItems.flatMap((item) => [
        {
            product_id: item.bookableItemId,
            product_name: item.bookableItemNameDe,
            category: getCartItemGtmCategory(item),
            price_net: getCartItemGtmPrice(item) / 1.19,
            price_gross: getCartItemGtmPrice(item),
            quantity: 1,
        },
        ...item.addOns.map((addOn) => ({
            product_id: addOn.addOnId,
            product_name: addOn.addOnNameDe,
            category: '',
            price_net: addOn.price / 1.19,
            price_gross: addOn.price,
            quantity: 1,
        })),
    ]);

// Helper to calculate total duration of a cart item including add-ons
export const getCartItemTotalDuration = (item: CartItem): number => {
    const addOnsDuration = item.addOns.reduce((sum, addon) => sum + addon.durationMinutes, 0);
    return item.durationMinutes + addOnsDuration;
};

// Helper to check if a cart item is applicable for a matched discount code
const isCartItemApplicable = (item: CartItem, matched: MatchedDiscountCode): boolean => {
    // Bundle redemption items are never discountable.
    if (item.isBundleRedemption) {
        return false;
    }
    if (matched.applicableBookableItemIds.includes(item.bookableItemId)) {
        return true;
    }
    if (item.bundleId && matched.applicableBundleIds.includes(item.bundleId)) {
        return true;
    }
    return false;
};

// Helper to calculate per-item discount amounts across the whole cart (PER_LINE_ITEM only).
// Returns an array aligned to cartItems. ABSOLUTE_DISCOUNT is spread proportionally by price
// across applicable items (remainder on last), matching the server and billing draft behavior.
export const getPerItemDiscountAmounts = (cartItems: CartItem[], matched: MatchedDiscountCode | null): number[] => {
    if (!matched || matched.applicationLevel !== 'PER_LINE_ITEM') {
        return cartItems.map(() => 0);
    }

    if (matched.type === 'PERCENTAGE_DISCOUNT') {
        return cartItems.map((item) => {
            if (!isCartItemApplicable(item, matched)) {
                return 0;
            }
            const itemPrice = getCartItemTotalPrice(item);
            return Math.round((itemPrice * matched.discountValue) / 100 * 100) / 100;
        });
    }

    // ABSOLUTE_DISCOUNT: spread proportionally across applicable items by price weight.
    const applicableIndices = cartItems
        .map((item, index) => ({ item, index }))
        .filter(({ item }) => isCartItemApplicable(item, matched));

    const amounts = cartItems.map(() => 0);
    const totalApplicablePrice = applicableIndices.reduce((sum, { item }) => sum + getCartItemTotalPrice(item), 0);
    if (totalApplicablePrice === 0) {
        return amounts;
    }

    let remainingDiscount = matched.discountValue;
    for (let i = 0; i < applicableIndices.length; i++) {
        const { item, index } = applicableIndices[i];
        const itemPrice = getCartItemTotalPrice(item);
        let allocated: number;
        if (i === applicableIndices.length - 1) {
            allocated = Math.round(remainingDiscount * 100) / 100;
        } else {
            const weight = itemPrice / totalApplicablePrice;
            allocated = Math.round(matched.discountValue * weight * 100) / 100;
        }
        allocated = Math.min(allocated, itemPrice);
        allocated = Math.max(allocated, 0);
        remainingDiscount -= allocated;
        amounts[index] = allocated;
    }

    return amounts;
};

// Helper to calculate entire-purchase discount amount
export const getEntirePurchaseDiscountAmount = (subtotal: number, matched: MatchedDiscountCode | null): number => {
    if (!matched) {
        return 0;
    }
    if (matched.applicationLevel !== 'ENTIRE_PURCHASE') {
        return 0;
    }
    if (matched.type === 'PERCENTAGE_DISCOUNT') {
        return Math.round((subtotal * matched.discountValue) / 100 * 100) / 100;
    }
    // ABSOLUTE_DISCOUNT: apply up to the subtotal
    return Math.min(matched.discountValue, subtotal);
};

export type SelectedStore = {
    id: number;
    name: string;
    slug: string;
    contactEmail: string;
    contactPhone: string;
    fullAddress?: string;
    description?: string;
    address?: string;
    city?: string;
    zipCode?: string;
    openingTimes?: Record<DayOfWeek, { openingTime: string; closingTime: string; } | null>;
    reviewsStarsOutOfFive?: number;
    reviewsCount?: number;
    images?: string[];
    clinicGoogleMapsImgSrc?: string;
    clinicGoogleMapsUrl?: string;
    federalState?: string | null;
    locationLatitude?: number;
    locationLongitude?: number;
};

export type SelectedPath = 'browse-by-category' | 'browse-by-symptom' | null;

// Resolved bookable item handed off from a deeplink into the real booking funnel.
// Structurally matches the BookableItem shape used by ServiceSelection / DeepLink
// (resolveDeeplink's bookableItemsByStoreId entries).
export type DeeplinkBookableItem = {
    id: string;
    nameDe: string;
    nameEn: string;
    descriptionDe: string;
    descriptionEn: string;
    type: 'SERVICE' | 'BUNDLE';
    keyBenefitsDe: string[];
    keyBenefitsEn: string[];
    youShouldKnowThisDe: string;
    youShouldKnowThisEn: string;
    order: number;
    images: { id: string; url: string; altText: string | null; order: number }[];
    variantOptions: {
        id: string; optionLabelDe: string; optionLabelEn: string; order: number;
        treatmentVariant: { id: string; name: string; priceCents: number; steps: { minuteEnd: number }[] };
    }[];
    addOns: {
        id: string; nameDe: string; nameEn: string; descriptionDe: string; descriptionEn: string;
        imageUrl: string | null; order: number;
        treatmentVariant: { id: string; name: string; priceCents: number; steps: { minuteEnd: number }[] };
    }[];
    bundle?: {
        id: string; name: string; unitPriceGross: number; unitQuantity: number; treatmentVariantId: string;
        treatmentVariant: { id: string; name: string; priceCents: number; steps: { minuteEnd: number }[] };
    } | null;
    availableBundles: {
        id: string; name: string; unitPriceGross: number; unitQuantity: number; treatmentVariantId: string;
        treatmentVariant: { id: string; name: string; priceCents: number; steps: { minuteEnd: number }[] };
    }[];
};

export type MatchedDiscountCode = {
    id: string; // discountCodeId for snapshot
    name: string;
    type: 'ABSOLUTE_DISCOUNT' | 'PERCENTAGE_DISCOUNT';
    discountValue: number;
    applicationLevel: 'PER_LINE_ITEM' | 'ENTIRE_PURCHASE';
    applicableBookableItemIds: string[];
    applicableBundleIds: string[];
};

const STORE_EXPIRY_MS = 24 * 60 * 60 * 1000; // 24 hours

// Define the booking flow state interface
export interface BookingFlowState {
    _timestamp: number;

    selectedStore: SelectedStore | null;
    setSelectedStore: (selectedStore: SelectedStore | null) => void;
    // Store the current cart items were added for
    cartStoreId: number | null;

    selectedPath: SelectedPath;
    setSelectedPath: (selectedPath: SelectedPath) => void;

    cartItems: CartItem[];
    setCartItems: (items: CartItem[]) => void;
    addCartItem: (item: CartItem) => void;
    removeCartItem: (bookableItemId: string) => void;
    clearCart: () => void;

    selectedTimeslot: LocalDateTime | null;
    setSelectedTimeslot: (selectedTimeslot: LocalDateTime | null) => void;

    scuid: string | null;
    setScuid: (scuid: string | null) => void;

    ip: string | null;
    setIp: (ip: string | null) => void;

    hasBookedBefore: boolean | null;
    setHasBookedBefore: (hasBookedBefore: boolean | null) => void;
    
    // Waitlist
    waitlistChecked: boolean;
    setWaitlistChecked: (waitlistChecked: boolean) => void;
    waitlistDate: Date | null;
    setWaitlistDate: (waitlistDate: Date | null) => void;

    firstName: string;
    setFirstName: (firstName: string) => void;
    lastName: string;
    setLastName: (lastName: string) => void;

    email: string;
    setEmail: (email: string) => void;

    phoneDialCode: string;
    setPhoneDialCode: (phoneDialCode: string) => void;

    phone: string;
    setPhone: (phone: string) => void;

    messageFromCustomer: string;
    setMessageFromCustomer: (messageFromCustomer: string) => void;

    discountCode: string;
    setDiscountCode: (discountCode: string) => void;

    matchedDiscountCode: MatchedDiscountCode | null;
    setMatchedDiscountCode: (matched: MatchedDiscountCode | null) => void;

    wantsEarlierTimeslot: boolean;
    setWantsEarlierTimeslot: (wantsEarlierTimeslot: boolean) => void;

    preferredContactTimes: string[];
    setPreferredContactTimes: (preferredContactTimes: string[]) => void;

    agreeTermsConditionsAndPrivacyPolicy: boolean;
    setAgreeTermsConditionsAndPrivacyPolicy: (agree: boolean) => void;

    paymentMethod: 'clinic' | 'klarna';
    setPaymentMethod: (paymentMethod: 'clinic' | 'klarna') => void;

    deeplinkSlug: string | null;
    setDeeplinkSlug: (slug: string | null) => void;

    deeplinkPractitionerId: number | null;
    setDeeplinkPractitionerId: (id: number | null) => void;

    preferredPractitionerId: number | null;
    setPreferredPractitionerId: (id: number | null) => void;

    deeplinkItem: DeeplinkBookableItem | null;
    setDeeplinkItem: (item: DeeplinkBookableItem | null) => void;

    // One-shot handoff: ServiceSelection opens this category, then clears it
    deeplinkCategoryId: string | null;
    setDeeplinkCategoryId: (id: string | null) => void;

    // Category a category deeplink points at; kept for the whole session for analytics tagging
    deeplinkTargetCategoryId: string | null;
    setDeeplinkTargetCategoryId: (id: string | null) => void;

    resetBookingFlow: () => void;
}

// Create the booking flow store with persistence
export const useBookingFlowStore = create<BookingFlowState>()(
    persist(
        (rawSet, get) => {
            const set: typeof rawSet = (partial, replace) => {
                if (typeof partial === 'function') {
                    rawSet((state) => ({ ...(partial as (s: BookingFlowState) => Partial<BookingFlowState>)(state), _timestamp: Date.now() }), replace);
                } else {
                    rawSet({ ...partial, _timestamp: Date.now() } as Partial<BookingFlowState>, replace);
                }
            };
            return {
            _timestamp: Date.now(),

            selectedStore: null,
            // Deselecting keeps the cart; picking a different store than the one the cart was built for empties it
            setSelectedStore: (selectedStore) => set((state) => {
                if (!selectedStore || selectedStore.id === (state.cartStoreId ?? state.selectedStore?.id)) {
                    return { selectedStore };
                }
                return { selectedStore, cartStoreId: selectedStore.id, cartItems: [] };
            }),
            cartStoreId: null,

            selectedPath: null,
            setSelectedPath: (selectedPath) => set({ selectedPath }),

            cartItems: [],
            setCartItems: (cartItems) => set({ cartItems }),
            addCartItem: (item) => set((state) => ({ cartItems: [...state.cartItems, item] })),
            removeCartItem: (bookableItemId) => set((state) => ({
                cartItems: state.cartItems.filter((item) => item.bookableItemId !== bookableItemId),
            })),
            clearCart: () => set({ cartItems: [] }),
            
            selectedTimeslot: null,
            setSelectedTimeslot: (selectedTimeslot) => set({ selectedTimeslot }),
            
            scuid: null,
            setScuid: (scuid) => set({ scuid }),

            ip: null,
            setIp: (ip) => set({ ip }),
            
            hasBookedBefore: false,
            setHasBookedBefore: (hasBookedBefore) => set({ hasBookedBefore }),
            
            waitlistChecked: false,
            setWaitlistChecked: (waitlistChecked) => set({ waitlistChecked }),
            waitlistDate: null,
            setWaitlistDate: (waitlistDate) => set({ waitlistDate }),
            
            firstName: "",
            setFirstName: (firstName) => set({ firstName }),
            lastName: "",
            setLastName: (lastName) => set({ lastName }),
            
            email: "",
            setEmail: (email) => set({ email }),
            
            phoneDialCode: "+49",
            setPhoneDialCode: (phoneDialCode) => set({ phoneDialCode }),
            
            phone: "",
            setPhone: (phone) => set({ phone }),
            
            messageFromCustomer: "",
            setMessageFromCustomer: (messageFromCustomer) => set({ messageFromCustomer }),
            
            discountCode: "",
            setDiscountCode: (discountCode) => set({ discountCode }),

            matchedDiscountCode: null,
            setMatchedDiscountCode: (matchedDiscountCode) => set({ matchedDiscountCode }),

            wantsEarlierTimeslot: false,
            setWantsEarlierTimeslot: (wantsEarlierTimeslot) => set({ wantsEarlierTimeslot }),

            preferredContactTimes: [],
            setPreferredContactTimes: (preferredContactTimes) => set({ preferredContactTimes }),

            agreeTermsConditionsAndPrivacyPolicy: false,
            setAgreeTermsConditionsAndPrivacyPolicy: (agreeTermsConditionsAndPrivacyPolicy) => set({ agreeTermsConditionsAndPrivacyPolicy }),

            paymentMethod: 'clinic',
            setPaymentMethod: (paymentMethod) => set({ paymentMethod }),

            deeplinkSlug: null,
            setDeeplinkSlug: (slug) => set({ deeplinkSlug: slug }),

            deeplinkPractitionerId: null,
            setDeeplinkPractitionerId: (id) => set({ deeplinkPractitionerId: id }),

            preferredPractitionerId: null,
            setPreferredPractitionerId: (id) => set({ preferredPractitionerId: id }),

            deeplinkItem: null,
            setDeeplinkItem: (deeplinkItem) => set({ deeplinkItem }),

            deeplinkCategoryId: null,
            setDeeplinkCategoryId: (deeplinkCategoryId) => set({ deeplinkCategoryId }),

            deeplinkTargetCategoryId: null,
            setDeeplinkTargetCategoryId: (deeplinkTargetCategoryId) => set({ deeplinkTargetCategoryId }),

            resetBookingFlow: () => set({
                selectedStore: null,
                cartStoreId: null,
                selectedPath: null,
                cartItems: [],
                selectedTimeslot: null,
                scuid: null,
                ip: null,
                hasBookedBefore: null,
                waitlistChecked: false,
                waitlistDate: null,
                firstName: "",
                lastName: "",
                email: "",
                phoneDialCode: "+49",
                phone: "",
                messageFromCustomer: "",
                discountCode: "",
                matchedDiscountCode: null,
                wantsEarlierTimeslot: false,
                preferredContactTimes: [],
                agreeTermsConditionsAndPrivacyPolicy: false,
                paymentMethod: 'clinic',
                deeplinkSlug: null,
                deeplinkPractitionerId: null,
                preferredPractitionerId: null,
                deeplinkItem: null,
                deeplinkCategoryId: null,
                deeplinkTargetCategoryId: null,
            }),
        }; },
        {
            name: 'booking-flow-store',
            storage: createJSONStorage(() => customStorage, {
                reviver: customReviver,
                replacer: customReplacer,
            }),
            partialize: (state) => ({
                _timestamp: state._timestamp,
                selectedStore: state.selectedStore,
                cartStoreId: state.cartStoreId,
                selectedPath: state.selectedPath,
                cartItems: state.cartItems,
                selectedTimeslot: state.selectedTimeslot,
                firstName: state.firstName,
                lastName: state.lastName,
                email: state.email,
                phoneDialCode: state.phoneDialCode,
                phone: state.phone,
                wantsEarlierTimeslot: state.wantsEarlierTimeslot,
                preferredContactTimes: state.preferredContactTimes,
                waitlistChecked: state.waitlistChecked,
                waitlistDate: state.waitlistDate,
                messageFromCustomer: state.messageFromCustomer,
                discountCode: state.discountCode,
                matchedDiscountCode: state.matchedDiscountCode,
                deeplinkSlug: state.deeplinkSlug,
                deeplinkPractitionerId: state.deeplinkPractitionerId,
                preferredPractitionerId: state.preferredPractitionerId,
                deeplinkItem: state.deeplinkItem,
                paymentMethod: state.paymentMethod,
                deeplinkCategoryId: state.deeplinkCategoryId,
                deeplinkTargetCategoryId: state.deeplinkTargetCategoryId,
            }),
            version: 4,
            onRehydrateStorage: () => (state) => {
                if (!state) return;
                const timestamp = state._timestamp;
                if (!timestamp || Date.now() - timestamp > STORE_EXPIRY_MS) {
                    state.resetBookingFlow();
                }
            },
        }
    )
);

